import React, { useEffect, useMemo } from 'react';
import { Col, Form, message, Row, Space } from 'antd';
import { useDispatch, useIntl, useSelector } from 'umi';
import ProForm, { ProFormText, ProFormTextArea } from '@ant-design/pro-form';
import InfoCard from './InfoCard';
import ExtraFieldSelector from '@/components/ExtraFields/ExtraFieldSelector';
import DynamicExtraFieldsForm from '@/components/ExtraFields/ExtraFieldDynamicForm';
import { ExtraFieldTypeModel } from '@/typings/models/ExtraFields';
import { setExtraFieldValueRequest } from '@/services/api/extraFieldType';
import { selectExtraFieldTypesList } from '@/selectors/extraFieldsSelectors';
import { mergeAutoAttachedExtraFields } from '@/utils/extraFields';
import styles from './ProjectInfoCard.less';
import isEmpty from 'lodash/isEmpty';
import { F } from '@/utils/intl';
import { SnippetsOutlined } from '@ant-design/icons';
import { LeadDetailsModel } from '@/typings/models/Lead';

export interface ProjectInfoCardProps {
  projectInfo: LeadDetailsModel;
  leadId?: string;
  onCardClick?: () => void;
  onRefresh?: () => void;
}

const ProjectInfoCard: React.FC<ProjectInfoCardProps> = ({
  projectInfo,
  leadId,
  onRefresh,
}) => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const intl = useIntl();

  const notes = projectInfo?.customer?.notes || projectInfo.notes || '';
  // Fetch extra field types on mount
  useEffect(() => {
    dispatch({
      type: 'extraFields/fetchExtraFieldTypes',
    });
  }, [dispatch]);

  // Initialize form with project data
  useEffect(() => {
    if (projectInfo) {
      form.setFieldsValue({
        notes,
        order_volume: projectInfo.order_volume || 0,
        title: projectInfo.title,
      });
    }
  }, [projectInfo, form]);

  const allExtraFieldTypes = useSelector(selectExtraFieldTypesList);

  const attachedExtraFields = useMemo(() => {
    const withValues = (projectInfo?.result_extra_fields || []).filter(
      (field) => !isEmpty(field.values),
    );
    return mergeAutoAttachedExtraFields(withValues, allExtraFieldTypes, 'result');
  }, [projectInfo?.result_extra_fields, allExtraFieldTypes]);

  // Get already selected field UUIDs for duplicate prevention
  const selectedFieldIds = useMemo(() => {
    return attachedExtraFields.map((field: any) => field.uuid);
  }, [attachedExtraFields]);

  // Handle field selection
  const handleFieldSelect = async (field: ExtraFieldTypeModel) => {
    if (!leadId) return;

    try {
      // Initialize the field with an empty value
      await setExtraFieldValueRequest({
        params: {
          extraFieldTypeId: field.uuid,
          leadId: leadId,
        },
        data: {
          value: field.field_type === 'bool' ? false : null,
        },
      });

      // Refresh lead data to show the new field
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      message.error(
        intl.formatMessage({
          id: 'pages.extraFieldType.addedError',
          defaultMessage: 'Error adding field',
        })
      );
    } finally {
    }
  };

  const handleFieldSubmit = async (fieldName: string, value: any) => {
    if (!leadId) return;


    try {
      // Dispatch Redux action to update lead information
      dispatch({
        type: 'leads/edit',
        payload: {
          leadId,
          [fieldName]: value,
        },
        then: () => {
          if (onRefresh) {
            onRefresh();
          }
        },
      });
    } catch (error) {
    }
  };

  return (
    <InfoCard
      title={
        <Space>
          <SnippetsOutlined />
          <F
            id={'pages.lead.project_information'}
            defaultMessage={'Customer Information'}
          />
        </Space>
      }
      variant="purple"
      className={styles.projectCardContainer}
    >
      <ProForm
        form={form}
        autoComplete="off"
        size="small"
        submitter={false}
        variant="underlined"
      >
        <Row gutter={8}>
          <Col span={24}>
            <ProFormText
              label={intl.formatMessage({
                id: 'common.attribute.title',
                defaultMessage: 'Title',
              })}
              name="title"
              fieldProps={{
                onBlur: async (e) => {
                  const value = e.target.value
                  if (value !== projectInfo.title) {
                    handleFieldSubmit('title', value);
                  }
                },
              }}
            />
          </Col>
          <Col span={24}>
            <ProFormTextArea
              label={intl.formatMessage({
                id: 'pages.lead.fields.note',
                defaultMessage: 'Note',
              })}
              name="notes"
              fieldProps={{
                rows: 3,
                onBlur: async (e) => {
                  const value = e.target.value;
                  if (value !== projectInfo.notes) {
                    handleFieldSubmit('customer.notes', value);
                  }
                },
              }}
            />
          </Col>
        </Row>

        {/* Extra Fields Section */}
        {attachedExtraFields && attachedExtraFields.length > 0 && (
          <Row gutter={8}>
            <Col span={24}>
              <DynamicExtraFieldsForm
                fields={attachedExtraFields}
                leadId={leadId}
                labelWidth={240}
                formProps={{ layout: 'vertical' }}
                removable
                grid
                onRefresh={onRefresh}
              />
            </Col>
          </Row>
        )}

        {/* Add Extra Field Selector */}
        <Row gutter={8}>
          <Col span={24}>
            <ExtraFieldSelector
              relatedTo="result"
              selectedFieldIds={selectedFieldIds}
              onFieldSelect={handleFieldSelect}
              onFieldCreated={handleFieldSelect}
            />
          </Col>
        </Row>
      </ProForm>
    </InfoCard>
  );
};

export default ProjectInfoCard;
